import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as AdaptiveCards from "adaptivecards";
import markdownit from "markdown-it";
import Switch from "@material-ui/core/Switch";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { useAppContext } from "../../api/AppContext";
// import * as hostConfigLight from "./microsoft-teams-light.json";
// CUSTOMIZATIONS:
// dark default from  to #242424
// light default from  to #ffffff
// accent default from #6264a7 to #4f52b2
// good default from #92c353 to #0e700e
// warning default from #f8d22a to #c43501 (it would be #e9835e for dark mode) (yellow to orange)
// attention default from #c4314b to #c50f1f
import * as hostConfigLight from "./custom-microsoft-teams-light.json";

import IFrame from "../shared/IFrame";

import "./teams-container-light.css";
import Loader from "../shared/Loader";

// Match Teams behavior: single \n in TextBlock markdown renders as a line break.
const markdownRenderer = markdownit({ breaks: true });

AdaptiveCards.AdaptiveCard.onProcessMarkdown = function (text, result) {
  result.outputHtml = markdownRenderer.render(text);
  result.didProcess = true;
};

function CardContainer({ child }) {
  const containerRef = useRef(null);
  useEffect(() => {
    if (child && containerRef.current)
      if (!containerRef.current.firstChild) {
        containerRef.current.appendChild(child);
      } else {
        containerRef.current.firstChild.replaceWith(child);
      }
  }, [child]);

  return <div ref={containerRef}></div>;
}

const Preview = ({
  semantic,
  semanticId,
  userId,
  src,
  defaultWidth,
  defaultHeight,
  children,
  isClientPreviewDefault = true,
  viewType,
}) => {
  const ref = useRef();

  const [width, setWidth] = useState(defaultWidth);
  const [height, setHeight] = useState(defaultHeight);
  const [isExportImageProcessing, setIsExportImageProcessing] = useState(false);
  const [isSizeReceived, setIsSizeReceived] = useState(false);

  const [isClientPreview, setIsClientPreview] = useState(
    viewType ? viewType === "client" : isClientPreviewDefault
  );

  useEffect(() => {
    if (viewType) {
      setIsClientPreview(viewType === "client");
    }
  }, [viewType]);

  const { exportImage } = useAppContext();

  useEffect(() => {
    const listener = (e) => {
      try {
        const { data } = e;

        if (data.type !== "rendered") {
          return;
        }

        setWidth(data.width);
        setHeight(data.height);
        setIsSizeReceived(true);
        // eslint-disable-next-line no-empty
      } catch {}
    };
    window.addEventListener("message", listener);

    return () => window.removeEventListener("message", listener);
  }, []);

  const [card, setCard] = useState();
  const [htmlCard, sethtmlCard] = useState(null);

  // Refs to store the latest values and timer
  // storedValuesRef always contains the most recent values, ensuring no data loss
  // even when rapid changes occur - the timer will use the latest stored values
  const storedValuesRef = useRef({ semantic, semanticId, userId });
  const timerRef = useRef(null);
  const isFirstRunRef = useRef(true);

  useEffect(() => {
    if ((!semantic && !semanticId) || isClientPreview) {
      return;
    }

    // Store the latest values - this ensures we always have the most recent data
    // even if the effect is called multiple times rapidly
    storedValuesRef.current = { semantic, semanticId, userId };

    // Function to execute the fetch with stored values
    const executeFetch = () => {
      setCard(null);

      // If running on localhost, return early
      if (
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1"
      ) {
        console.log(
          "Card preview not yet supported on localhost (endpoint cors errors)"
        );
        return;
      }

      const {
        semantic: storedSemantic,
        semanticId: storedSemanticId,
        userId: storedUserId,
      } = storedValuesRef.current;

      fetch(
        process.env[
          `REACT_APP_account_${process.env.REACT_APP_DASHBOARD_NAME.toLowerCase()}_dashboard_endpoints_teams_get_card`
        ],
        // e.g.: "https://teams.knowledgehubapp.habitinspiringplatform.com/api/get-card",
        {
          body: JSON.stringify({
            semantic: storedSemantic,
            semanticId: storedSemanticId,
            userId: storedUserId,
          }),
          headers: {
            "Content-Type": "application/json",
          },
          method: "POST",
        }
      )
        .then((response) => response.json())
        .then((body) => setCard(body.card));
    };

    // Execute immediately on first run
    if (isFirstRunRef.current) {
      isFirstRunRef.current = false;
      executeFetch();
    }

    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Set up timer to execute fetch after 500ms with latest stored values
    timerRef.current = setTimeout(() => {
      executeFetch();
      timerRef.current = null;
    }, 500);

    // Cleanup function to clear timer on unmount or dependency change
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isClientPreview, semantic, semanticId, userId]);

  useEffect(() => {
    if (card) {
      const adaptiveCard = new AdaptiveCards.AdaptiveCard();

      // Fix preview issue for easyPOP templates
      const easyPopLogo = "t_a4bb4656-2e4d-4e69-a542-9145cf2dcddb.png";
      const easyPopLogoPreview =
        "t_a4bb4656-2e4d-4e69-a542-9145cf2dcddb_preview.png";
      const cardToRender =
        semantic?.teams?.semanticTemplateId ===
          "crd-70443292-2541-44df-8c3e-f4fab4dc53c2" ||
        semantic?.teams?.semanticTemplateId ===
          "crd-7bdd0769c-b817-44eb-8bcd-ba129a5a47cc"
          ? JSON.parse(
              JSON.stringify(card).replaceAll(easyPopLogo, easyPopLogoPreview)
            )
          : card;

      // Custom hostConfig for family2 and easyPop templates
      /*if (
        semantic.teams?.templateFamilyId &&
        (semantic.teams?.templateFamilyId ===
          "fam-dc423757-effc-4510-ac5a-9c9f4c9ec268" ||
          semantic.teams?.templateFamilyId ===
            "fam-44197fb8-7a0b-4079-a76f-25210e68db4e")
      ) {
        adaptiveCard.hostConfig = new AdaptiveCards.HostConfig(
          hostConfigFamily2
        );
      } else {
        // Set its hostConfig property unless you want to use the default Host Config
        // Host Config defines the style and behavior of a card
        adaptiveCard.hostConfig = new AdaptiveCards.HostConfig(hostConfigLight);
      }*/

      // Set its hostConfig property unless you want to use the default Host Config
      // Host Config defines the style and behavior of a card
      adaptiveCard.hostConfig = new AdaptiveCards.HostConfig(hostConfigLight);

      // Set the adaptive card's event handlers. onExecuteAction is invoked
      // whenever an action is clicked in the card
      adaptiveCard.onExecuteAction = function (action) {
        alert(`Cta clicked! Action would be => ${JSON.stringify(action)}`);
      };
      // Parse the card payload
      adaptiveCard.parse(cardToRender);
      sethtmlCard(adaptiveCard.render());
    }
  }, [card, hostConfigLight, semantic]);

  const reload = useCallback(() => {
    setIsSizeReceived(false);
    ref.current?.contentWindow.location.replace(src);
  }, [src]);

  const callExportImageLambda = useCallback(async () => {
    setIsExportImageProcessing(true);

    const exportImageRequest = {
      clip: {
        height,
        width,
        x: 0,
        y: 0,
      },
      src,
    };
    // console.log("exportImageRequest: " + JSON.stringify(exportImageRequest));
    const result = await exportImage(exportImageRequest);

    if (result.imageB64) {
      console.log("there is a result");
      const linkSource = `data:image/png;base64,${result.imageB64}`;
      const downloadLink = document.createElement("a");
      const fileName = `export-${semantic.id}.png`;
      downloadLink.href = linkSource;
      downloadLink.download = fileName;
      downloadLink.click();
    }
    setIsExportImageProcessing(false);
  }, [src, semantic, width, height, exportImage]);

  const openExportImage = useCallback(() => {
    const encodedParams = window.btoa(JSON.stringify(src));
    window.open(`/export?url=${encodedParams}`);
  }, [src]);

  const iframe = useMemo(
    () => (
      <Grid container spacing={2}>
        {!viewType && (
          <Grid item xs={12}>
            <Grid container alignItems="center" component="label" spacing={1}>
              <Grid item>
                <Typography
                  color={isClientPreview ? "default" : "primary"}
                  variant="button"
                >
                  Teams App
                </Typography>
              </Grid>
              <Grid item>
                <Switch
                  checked={isClientPreview}
                  color="primary"
                  onChange={(e) => setIsClientPreview(e.target.checked)}
                />
              </Grid>
              <Grid item>
                <Typography
                  color={isClientPreview ? "primary" : "default"}
                  variant="button"
                >
                  Client
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        )}
        <Grid item xs={12}>
          {isClientPreview ? (
            <IFrame
              ref={ref}
              height={height}
              id="iframe"
              src={src}
              title="preview"
              width={width}
            />
          ) : (
            <>
              {htmlCard ? (
                // <AdaptiveCard hostConfig={{}} payload={card} />
                <div
                  className="teams-inner-frame"
                  // style={{ backgroundColor: "white" }}
                >
                  <CardContainer child={htmlCard} />
                </div>
              ) : (
                <Loader inline />
              )}
            </>
          )}
        </Grid>
      </Grid>
    ),
    [viewType, isClientPreview, height, src, width, htmlCard]
  );

  return children !== undefined
    ? children({
        callExportImageLambda,
        height,
        iframe,
        isExportImageProcessing,
        isSizeReceived,
        openExportImage,
        reload,
        width,
      })
    : iframe;
};

export default Preview;
